// TS twin of iOS `WheelScale`.
// Per-axis scale factors for an AR wheel — face (diameter) and width scale
// independently so a 22x10 wheel renders visibly narrower than a 22x12.
// Convention (matches iOS normalizeWheelOrientation): axle is +Z, wheel face
// spans X+Y. Axis vector is therefore (face, face, width); renderers feed
// this straight into scene.scale on the wheel root.

import type { WheelSize } from '@/models/wheel-size'

export interface WheelScale {
  readonly face: number
  readonly width: number
}

export const WHEEL_SCALE_IDENTITY: WheelScale = { face: 1, width: 1 }

export function wheelScale(face: number, width: number): WheelScale {
  return { face, width }
}

export function wheelScaleAxisVector(
  scale: WheelScale,
): readonly [number, number, number] {
  return [scale.face, scale.face, scale.width]
}

// Mirrors iOS `WheelInfo.currentSizeScale` math (WheelInfo.swift:52-70).
// Returns the per-axis scale that resizes a wheel assembled at `reference`
// to render at `selected` dimensions. Width falls back to the face ratio
// when either reference.widthInches or selected.widthInches is missing.
export function wheelScaleFromSizes(
  reference: WheelSize | null,
  selected: WheelSize | null,
): WheelScale {
  if (
    reference == null ||
    selected == null ||
    reference.diameterInches <= 0
  ) {
    return WHEEL_SCALE_IDENTITY
  }
  const face = selected.diameterInches / reference.diameterInches
  const refWidth = reference.widthInches
  const selWidth = selected.widthInches
  const width =
    refWidth != null && refWidth > 0 && selWidth != null
      ? selWidth / refWidth
      : face
  return wheelScale(face, width)
}
